// src/pages/Privacy.js - Updated for Cloud Functions
// ✅ When admin marks a deletion request as "completed",
//    Cloud Function onAccountDeletionRequested automatically:
//    → Deletes all user data from Firestore
//    → Deletes Firebase Auth account
//    → Deletes CDL photos from Storage
//    → Sends PIPEDA confirmation email to user
import React, { useState, useEffect } from "react";
import { collection, getDocs, doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";

export default function Privacy() {
const [requests, setRequests] = useState([]);
const [loading, setLoading] = useState(true);
const [filter, setFilter] = useState("pending");
const [stats, setStats] = useState({ pending: 0, completed: 0, deletion: 0, access: 0 });

const load = async () => {
setLoading(true);
try {
const snap = await getDocs(collection(db, "dataRequests"));
const data = snap.docs.map(d => ({ id: d.id, ...d.data() }))
.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
setRequests(data);
setStats({
pending: data.filter(r => r.status === "pending").length,
completed: data.filter(r => r.status === "completed").length,
deletion: data.filter(r => r.type === "deletion").length,
access: data.filter(r => r.type === "access").length,
});
} catch (err) { console.error(err); }
finally { setLoading(false); }
};

useEffect(() => { load(); }, []);

const filtered = filter === "all" ? requests
: requests.filter(r => r.status === filter || r.type === filter);

// ✅ For ACCESS requests: admin manually emails data then marks complete
// ✅ For DELETION requests: just mark complete — Cloud Function does ALL the deletion automatically
const handleComplete = async (request) => {
if (request.type === "deletion") {
const confirmed = window.confirm(
`Mark deletion complete for ${request.userEmail}?\n\n` +
`⚠️ IMPORTANT: When you click OK, the Cloud Function will automatically:\n` +
`• Delete all user data from Firestore\n` +
`• Delete their Firebase Auth account\n` +
`• Delete their CDL photos from Storage\n` +
`• Send a PIPEDA deletion confirmation email\n\n` +
`This cannot be undone.`
);
if (!confirmed) return;
} else {
const confirmed = window.confirm(
`Confirm you have emailed all personal data to ${request.userEmail}?`
);
if (!confirmed) return;
}

try {
  await updateDoc(doc(db, 'dataRequests', request.id), {
    status: 'completed',
    completedAt: serverTimestamp(),
    completedBy: 'admin',
  });

  if (request.type === 'deletion') {
    alert(`✅ Deletion request marked complete.\n\nCloud Function is now automatically purging all data for ${request.userEmail} and sending them a confirmation email.`);
  } else {
    alert(`✅ Access request marked complete for ${request.userEmail}.`);
  }
  load();
} catch (err) {
  alert('Error updating request. Please try again.');
}

};

const handleNotifyBreach = async () => {
const email = window.prompt("Enter affected user email (or “all” for all users):");
if (!email) return;
const description = window.prompt("Describe the breach briefly:");
if (!description) return;
try {
await updateDoc(doc(db, "settings", "breachLog"), {
[`breach_${Date.now()}`]: {
affectedUsers: email,
description,
reportedAt: new Date().toISOString(),
reportedBy: "admin",
status: "reported",
}
});
alert("⚠️ Breach logged.\n\nRemember:\n• Notify affected users within 72 hours\n• Report to the OPC at www.priv.gc.ca");
} catch (err) {
alert("Error logging breach. Contact privacy@crewboard.ca immediately.");
}
};

const timeAgo = (ts) => {
if (!ts?.seconds) return "recently";
const diff = Math.floor((Date.now() / 1000 - ts.seconds) / 3600);
if (diff < 24) return `${diff}h ago`;
return `${Math.floor(diff / 24)}d ago`;
};

const TYPE_CONFIG = {
deletion: { label: "🗑️ Deletion Request", color: "#EF4444", bg: "#EF444420" },
access: { label: "📥 Data Access Request", color: "#3B82F6", bg: "#3B82F620" },
};
const STATUS_CONFIG = {
pending: { label: "⏳ Pending", color: "#EAB308", bg: "#EAB30820" },
completed: { label: "✅ Completed", color: "#22C55E", bg: "#22C55E20" },
in_progress: { label: "🔄 In Progress", color: "#F97316", bg: "#F9731620" },
};

return (
<div className="animate-fade">
<div style={{ marginBottom: 28 }}>
<h1 style={{ fontFamily: "Syne", fontSize: 28, fontWeight: 800, color: "var(--text)" }}>Privacy & PIPEDA</h1>
<p style={{ color: "var(--text-muted)", marginTop: 4 }}>Manage data access and deletion requests</p>
</div>

  {/* Cloud Function notice */}
  <div style={{ background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.2)', borderRadius: 12, padding: '16px 20px', marginBottom: 20 }}>
    <div style={{ color: '#22C55E', fontWeight: 700, marginBottom: 8 }}>☁️ Automated PIPEDA Deletion</div>
    <div style={{ color: 'var(--text-secondary)', fontSize: 13, lineHeight: 1.7 }}>
      For <strong style={{ color: '#EF4444' }}>Deletion Requests</strong>: Simply mark the request as "Complete" — the Cloud Function <code style={{ color: '#F97316' }}>onAccountDeletionRequested</code> will automatically delete ALL user data (Firestore, Auth, Storage) and send the PIPEDA confirmation email.<br />
      For <strong style={{ color: '#3B82F6' }}>Access Requests</strong>: Manually export and email the user's data, then mark as complete.
    </div>
  </div>

  {/* PIPEDA Legal Banner */}
  <div style={{ background: 'rgba(234,179,8,0.08)', border: '1px solid rgba(234,179,8,0.25)', borderRadius: 12, padding: '16px 20px', marginBottom: 24 }}>
    <div style={{ color: '#EAB308', fontWeight: 700, marginBottom: 6 }}>⚖️ PIPEDA Legal Requirements</div>
    <div style={{ color: 'var(--text-secondary)', fontSize: 13, lineHeight: 1.6 }}>
      • Respond to data access requests within <strong style={{ color: 'var(--text)' }}>30 days</strong><br />
      • Complete account deletion within <strong style={{ color: 'var(--text)' }}>30 days</strong> of request<br />
      • Report data breaches to the OPC within <strong style={{ color: 'var(--text)' }}>72 hours</strong><br />
      • Contact: <strong style={{ color: 'var(--text)' }}>privacy@crewboard.ca</strong>
    </div>
  </div>

  {/* Stats */}
  <div style={{ display: 'flex', gap: 16, marginBottom: 28, flexWrap: 'wrap' }}>
    {[
      { icon: '⏳', label: 'Pending Requests', value: stats.pending, color: '#EAB308' },
      { icon: '✅', label: 'Completed', value: stats.completed, color: '#22C55E' },
      { icon: '🗑️', label: 'Deletion Requests', value: stats.deletion, color: '#EF4444' },
      { icon: '📥', label: 'Access Requests', value: stats.access, color: '#3B82F6' },
    ].map(s => (
      <div key={s.label} style={{ flex: 1, minWidth: 160, background: 'var(--surface)', border: `1px solid ${s.color}22`, borderRadius: 14, padding: 20 }}>
        <div style={{ fontSize: 24, marginBottom: 8 }}>{s.icon}</div>
        <div style={{ fontSize: 32, fontWeight: 800, color: s.color, fontFamily: 'Syne' }}>{s.value}</div>
        <div style={{ color: 'var(--text-secondary)', fontSize: 12, marginTop: 4 }}>{s.label}</div>
      </div>
    ))}
  </div>

  {/* Actions */}
  <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
    <button onClick={handleNotifyBreach} style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#EF4444', borderRadius: 10, padding: '12px 20px', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>
      ⚠️ Log Data Breach
    </button>
    <a href="https://www.priv.gc.ca/en/report-a-concern/report-a-privacy-breach-as-an-organization/" target="_blank" rel="noreferrer"
      style={{ background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.3)', color: '#3B82F6', borderRadius: 10, padding: '12px 20px', fontWeight: 700, fontSize: 14, textDecoration: 'none' }}>
      🏛️ Report to OPC →
    </a>
  </div>

  {/* Filters */}
  <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
    {['pending', 'completed', 'deletion', 'access', 'all'].map(f => (
      <button key={f} onClick={() => setFilter(f)} style={{
        background: filter === f ? '#F97316' : 'var(--surface)',
        border: `1px solid ${filter === f ? '#F97316' : 'var(--border)'}`,
        color: filter === f ? '#fff' : 'var(--text-secondary)',
        borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', textTransform: 'capitalize',
      }}>{f.charAt(0).toUpperCase() + f.slice(1)}</button>
    ))}
  </div>

  {/* Requests */}
  {loading ? (
    <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 60 }}>Loading requests...</div>
  ) : filtered.length === 0 ? (
    <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 60, background: 'var(--surface)', borderRadius: 16, border: '1px solid var(--surface2)' }}>
      <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
      <div>No {filter} requests</div>
    </div>
  ) : (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {filtered.map(req => {
        const typeConf = TYPE_CONFIG[req.type] || { label: req.type, color: 'var(--text-secondary)', bg: 'rgba(136,136,136,0.12)' };
        const statusConf = STATUS_CONFIG[req.status] || { label: req.status, color: 'var(--text-secondary)', bg: 'rgba(136,136,136,0.12)' };
        const daysAgo = req.createdAt?.seconds ? Math.floor((Date.now() / 1000 - req.createdAt.seconds) / 86400) : 0;
        const isOverdue = req.status === 'pending' && daysAgo > 25;
        const createdAt = req.createdAt?.toDate?.()?.toLocaleDateString() || 'Unknown';

        return (
          <div key={req.id} style={{ background: 'var(--surface)', border: `1px solid ${isOverdue ? '#EF4444' : 'var(--surface2)'}`, borderRadius: 14, padding: 20 }}>
            {isOverdue && (
              <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8, padding: '8px 12px', marginBottom: 14, color: '#EF4444', fontSize: 12, fontWeight: 600 }}>
                ⚠️ OVERDUE — {30 - daysAgo} days remaining to comply with PIPEDA
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                  <span style={{ background: typeConf.bg, color: typeConf.color, borderRadius: 6, padding: '3px 10px', fontSize: 12, fontWeight: 700 }}>{typeConf.label}</span>
                  <span style={{ background: statusConf.bg, color: statusConf.color, borderRadius: 6, padding: '3px 10px', fontSize: 12, fontWeight: 700 }}>{statusConf.label}</span>
                </div>
                <div style={{ color: 'var(--text)', fontWeight: 600, marginBottom: 4 }}>{req.userEmail}</div>
                <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>User ID: {req.userId}</div>
                <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 2 }}>Submitted: {createdAt} ({daysAgo} days ago)</div>

                {/* Cloud Function note for deletion */}
                {req.type === 'deletion' && req.status === 'pending' && (
                  <div style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)', borderRadius: 8, padding: '8px 12px', marginTop: 10, fontSize: 12, color: '#EF4444' }}>
                    ☁️ Clicking "Mark Complete" will trigger Cloud Function to automatically delete ALL user data
                  </div>
                )}

                {req.type === 'access' && req.status === 'pending' && (
                  <div style={{ background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.15)', borderRadius: 8, padding: '8px 12px', marginTop: 10, fontSize: 12, color: '#3B82F6' }}>
                    📧 Export user data from Firebase Console and email to {req.userEmail}, then mark complete
                  </div>
                )}

                {req.completedAt && (
                  <div style={{ color: '#22C55E', fontSize: 12, marginTop: 4 }}>
                    Completed: {req.completedAt?.toDate?.()?.toLocaleDateString()}
                  </div>
                )}
              </div>

              {req.status === 'pending' && (
                <button onClick={() => handleComplete(req)} style={{
                  background: req.type === 'deletion' ? 'rgba(239,68,68,0.1)' : 'rgba(34,197,94,0.1)',
                  border: `1px solid ${req.type === 'deletion' ? 'rgba(239,68,68,0.3)' : 'rgba(34,197,94,0.3)'}`,
                  color: req.type === 'deletion' ? '#EF4444' : '#22C55E',
                  borderRadius: 8, padding: '10px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}>
                  {req.type === 'deletion' ? '🗑️ Mark Complete' : '✅ Mark Sent'}
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  )}

  {/* How to Handle Guide */}
  <div style={{ background: 'var(--surface)', border: '1px solid var(--surface2)', borderRadius: 16, padding: 24, marginTop: 32 }}>
    <h3 style={{ fontFamily: 'Syne', color: 'var(--text)', marginBottom: 20 }}>📘 How to Handle PIPEDA Requests</h3>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
      <div>
        <div style={{ color: '#3B82F6', fontWeight: 700, marginBottom: 10 }}>📥 Data Access Request</div>
        {[
          '1. Go to Firebase Console → Firestore',
          '2. Search for user by their User ID',
          '3. Export data from: users, applications, reviews',
          '4. Email a copy to the user',
          '5. Click "Mark Sent" — done in 30 days ✅',
        ].map((s, i) => (
          <div key={i} style={{ color: 'var(--text-secondary)', fontSize: 12, padding: '6px 0', borderBottom: '1px solid var(--surface2)' }}>{s}</div>
        ))}
      </div>
      <div>
        <div style={{ color: '#EF4444', fontWeight: 700, marginBottom: 10 }}>🗑️ Deletion Request</div>
        {[
          '1. Click "Mark Complete" in this panel',
          '2. ☁️ Cloud Function automatically:',
          '   → Deletes all Firestore documents',
          '   → Deletes Firebase Auth account',
          '   → Deletes CDL photos from Storage',
          '   → Emails PIPEDA confirmation',
          '3. Done! No manual steps needed ✅',
        ].map((s, i) => (
          <div key={i} style={{ color: 'var(--text-secondary)', fontSize: 12, padding: '6px 0', borderBottom: '1px solid var(--surface2)' }}>{s}</div>
        ))}
      </div>
    </div>
  </div>
</div>

);
}